import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Button, Grid, Box, CircularProgress, Stack, Typography, Tooltip } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { gotoPreviousMenu } from '../../../store/assessment.slice';
import { RootState } from '../../../reducers';
import { fetchMemberDemoGraphicInfo } from '../../../store/memberDetail.slice';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { saveEngagementDraft } from '../../../store/documentEngagement.slice';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { copyObject, isValidFirstName, isValidName } from '../../../utils/common';
import { SummaryNoteTypeEnum } from '../../../Enum/SummaryNoteTypeEnum';
import MemberDetailEditPage from '../../Members/MemberDetail/MemberDetailEditPage';
import Authorization from '../../../utils/Authorization';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Vital_Signs_Care_Circle_Demographics_PCP.pdf';

const MemberAssessmentDemographics: React.FC<{ memberId: string }> = ({ memberId }) => {
    const dispatch = useAppDispatch();
    const auth = new Authorization();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const [isFetching, setisFetching] = useState<boolean>(true);
    const [saveClickEvent, setSaveClickEvent] = useState(null);
    const { isSaving, selectedMember } = useSelector((state: RootState) => state.memberDetail);
    const { engagementDraft, memberAssessmentHistory } = useSelector((state: RootState) => state.documentEngagement);
    const [memberCopy] = useState(selectedMember);

    useEffect(() => {
        dispatch(fetchMemberDemoGraphicInfo(memberId, () => setisFetching(false)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const valid = () => {
        if (
            !selectedMember.FirstName ||
            !selectedMember.LastName ||
            !selectedMember.DOB ||
            (auth.Role !== 'CEO' && memberCopy.FirstName !== selectedMember.FirstName && !isValidFirstName(selectedMember.FirstName)) ||
            (auth.Role === 'CEO' && !isValidName(selectedMember.FirstName)) ||
            !isValidName(selectedMember.LastName) ||
            !isValidName(selectedMember.MiddleName)
        ) {
            return false;
        } else {
            return true;
        }
    };

    const handleOnSubmit = () => {
        if (valid()) {
            setShowSaveAlert(true);
        } else {
            if (saveClickEvent) {
                saveClickEvent();
            }
        }
    };

    const handleOnConfirmToNext = (isWithoutComplete = false) => {
        if (saveClickEvent) {
            saveClickEvent();
        }

        if (isWithoutComplete) {
            let data = copyObject(engagementDraft);
            data.SummaryNotes = data.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.DemographicInfo) || [];
            data.SummaryNotes.push({
                Id: memberId,
                CptCode: '',
                Zcode: '',
                Note: 'Completed Demographic Information',
                Type: SummaryNoteTypeEnum.DemographicInfo,
                IsConfirm: true,
                OrderBy: 28,
                DisplayName: 'Demographic Info',
            });
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                DemographicStatus: StatusEnum.Completed,
            };
            const memberAssessmentHistoryUpdated = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                EngagementDraft: data,
            };

            dispatch(saveEngagementDraft(memberAssessmentHistoryUpdated));
        }
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel">
            {isFetching ? (
                <Box display="flex" justifyContent="center" className="loader-container">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 4 }}>
                        <Typography variant="h6">Demographics</Typography>
                        <Tooltip title="Demographics Help Document">
                            <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                        </Tooltip>
                    </Stack>
                    <Grid className="content-form">
                        <MemberDetailEditPage memberId={memberId} isAssessment={true} setSaveClickEvent={setSaveClickEvent} />
                    </Grid>
                    <Grid justifyContent="flex-end" className="content-footer">
                        <Button
                            variant="contained"
                            color="secondary"
                            className="mr-3"
                            disabled={isSaving}
                            onClick={() => dispatch(gotoPreviousMenu())}
                            startIcon={<ArrowBackIosIcon />}
                        >
                            Previous
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            onClick={handleOnSubmit}
                            disabled={isSaving}
                            endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIosIcon />}
                        >
                            Next
                        </Button>
                    </Grid>
                </>
            )}
            <AlertDialog
                open={showSaveAlert}
                title="Demographics"
                cancelText="Go Back"
                okText="Confirm"
                onConfirm={() => handleOnConfirmToNext(true)}
                onSkip={() => handleOnConfirmToNext()}
                onClose={() => setShowSaveAlert(false)}
                isLoading={isSaving}
                message="I confirm and acknowledge that Demographics is completed."
            />
        </Grid>
    );
};

export default MemberAssessmentDemographics;
